/** In-memory + localStorage tag appearance (color icon + country flags), synced to journey_meta. */

import { updateJourneyMeta } from '../services/api/journeysApi';
import {
  getTagIconId as readStoredIconId,
  migrateTagIcon,
  setTagIconId as writeStoredIconId,
  type TagIconId,
  TRAVEL_TAG_ICONS,
} from './tagIcons';

const FLAGS_STORAGE_KEY = 'mytraveldiary.tagCountries';
const MAX_FLAGS = 10;

export type TagAppearance = {
  iconId: TagIconId;
  countryIso2List: string[];
};

type Listener = () => void;

const cache = new Map<string, TagAppearance>();
const listeners = new Set<Listener>();
const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();
const seededIcons = new Set<string>();

const knownIcons = new Set(TRAVEL_TAG_ICONS.map((x) => x.id));

function notify(): void {
  listeners.forEach((cb) => {
    try {
      cb();
    } catch {
      /* ignore */
    }
  });
}

export function subscribeTagAppearance(cb: Listener): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

function readFlagsMap(): Record<string, string[]> {
  try {
    const raw = localStorage.getItem(FLAGS_STORAGE_KEY);
    const map = raw ? (JSON.parse(raw) as Record<string, unknown>) : {};
    const out: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(map)) {
      if (!Array.isArray(v)) continue;
      out[k] = normalizeIsoList(v as string[]);
    }
    return out;
  } catch {
    return {};
  }
}

function writeFlagsMap(map: Record<string, string[]>): void {
  try {
    localStorage.setItem(FLAGS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

export function normalizeIsoList(raw: string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of raw) {
    const code = String(item || '')
      .trim()
      .toUpperCase();
    if (!/^[A-Z]{2}$/.test(code) || seen.has(code)) continue;
    seen.add(code);
    out.push(code);
    if (out.length >= MAX_FLAGS) break;
  }
  return out;
}

function ensureEntry(tag: string): TagAppearance {
  const key = tag.trim();
  const existing = cache.get(key);
  if (existing) return existing;
  const flagsMap = readFlagsMap();
  const entry: TagAppearance = {
    iconId: readStoredIconId(key),
    countryIso2List: flagsMap[key] || [],
  };
  cache.set(key, entry);
  return entry;
}

export function getTagAppearance(tag: string | null | undefined): TagAppearance {
  const key = (tag || '').trim();
  if (!key) return { iconId: 'map-pin', countryIso2List: [] };
  return { ...ensureEntry(key), countryIso2List: [...ensureEntry(key).countryIso2List] };
}

export function getTagCountryIso2List(tag: string | null | undefined): string[] {
  return getTagAppearance(tag).countryIso2List;
}

function persistLocal(tag: string, appearance: TagAppearance): void {
  writeStoredIconId(tag, appearance.iconId);
  const map = readFlagsMap();
  if (appearance.countryIso2List.length) map[tag] = appearance.countryIso2List;
  else delete map[tag];
  writeFlagsMap(map);
}

function schedulePersist(tag: string, delayMs: number): void {
  const prev = pendingTimers.get(tag);
  if (prev) clearTimeout(prev);
  pendingTimers.set(
    tag,
    setTimeout(() => {
      pendingTimers.delete(tag);
      const appearance = cache.get(tag);
      if (!appearance) return;
      void updateJourneyMeta(tag, {
        iconId: appearance.iconId,
        countryIso2List: appearance.countryIso2List,
      }).catch((err) => {
        console.warn('tag appearance sync failed', err);
      });
    }, delayMs),
  );
}

export function setTagAppearance(
  tag: string,
  patch: { iconId?: TagIconId; countryIso2List?: string[] },
  opts?: { debounceMs?: number },
): void {
  const key = tag.trim();
  if (!key) return;
  const current = ensureEntry(key);
  const next: TagAppearance = {
    iconId:
      patch.iconId && knownIcons.has(patch.iconId) ? patch.iconId : current.iconId,
    countryIso2List:
      patch.countryIso2List !== undefined
        ? normalizeIsoList(patch.countryIso2List)
        : current.countryIso2List,
  };
  cache.set(key, next);
  persistLocal(key, next);
  notify();
  schedulePersist(key, opts?.debounceMs ?? (patch.countryIso2List !== undefined ? 300 : 0));
}

export function migrateTagAppearance(fromTag: string, toTag: string): void {
  const from = fromTag.trim();
  const to = toTag.trim();
  if (!from || !to || from === to) return;
  const appearance = ensureEntry(from);
  cache.set(to, { ...appearance, countryIso2List: [...appearance.countryIso2List] });
  persistLocal(to, cache.get(to)!);
  cache.delete(from);
  const map = readFlagsMap();
  delete map[from];
  writeFlagsMap(map);
  migrateTagIcon(from, to);
  notify();
}

export type JourneyAppearanceSource = {
  tag: string;
  iconId?: string | null;
  countryIso2List?: string[] | null;
};

/** Hydrate cache from GET /journeys; seed DB once from localStorage when meta icon is empty. */
export function hydrateTagAppearanceFromJourneys(items: JourneyAppearanceSource[]): void {
  for (const item of items) {
    const tag = (item.tag || '').trim();
    if (!tag) continue;
    const serverIcon =
      item.iconId && knownIcons.has(item.iconId as TagIconId)
        ? (item.iconId as TagIconId)
        : null;
    const serverFlags = normalizeIsoList(item.countryIso2List || []);
    const local = ensureEntry(tag);

    const iconId = serverIcon || local.iconId;
    const countryIso2List = serverFlags.length ? serverFlags : local.countryIso2List;

    cache.set(tag, { iconId, countryIso2List });
    persistLocal(tag, { iconId, countryIso2List });

    if (!serverIcon && local.iconId && !seededIcons.has(tag)) {
      seededIcons.add(tag);
      void updateJourneyMeta(tag, {
        iconId: local.iconId,
        countryIso2List: countryIso2List.length ? countryIso2List : undefined,
      }).catch(() => {
        seededIcons.delete(tag);
      });
    } else if (serverFlags.length === 0 && local.countryIso2List.length && !seededIcons.has(`flags:${tag}`)) {
      seededIcons.add(`flags:${tag}`);
      void updateJourneyMeta(tag, { countryIso2List: local.countryIso2List }).catch(() => {
        seededIcons.delete(`flags:${tag}`);
      });
    }
  }
  notify();
}

export { MAX_FLAGS as MAX_TAG_COUNTRY_FLAGS };
